import React, { Fragment, useRef, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Splitter } from "@progress/kendo-react-layout";
import { PanelBar, PanelBarItem } from "@progress/kendo-react-layout";
import {
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
  ToolbarSpacer,
  Button,
  DropDownButton,
} from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Loader } from "@progress/kendo-react-indicators";
import { DropDownListById } from "../../forms/form-components";
import { Form } from "../../components/form";
import { List } from "../../components/list";
import { Input } from "@progress/kendo-react-inputs";
import { AlertsPageForm } from "./alertspageform";
import { IntroPage } from "../intropage";
import { ReactComponent as ExclamationIcon } from "../../icons/exclamation-circle.svg";
import { ReactComponent as HeartIcon } from "../../icons/heart-rate.svg";
import { ReactComponent as TachometerIcon } from "../../icons/tachometer.svg";
import { ReactComponent as MessageIcon } from "../../icons/message.svg";
import { ReactComponent as GroupIcon } from "../../icons/layergroup.svg";
import { ReactComponent as FilterIcon } from "../../icons/filter.svg";
import { ReactComponent as BellIcon } from "../../icons/bell.svg";
import { ReactComponent as ActivateIcon } from "../../icons/activate.svg";
import {
  alertsMaxReached,
  canCreateAlerts,
  canCreateResolvedAlerts,
  canDeleteAlerts,
  canEditAlerts,
  canViewAlerts,
  canViewResolvedAlerts,
} from "../../routing/authorizationhandlers";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

const datasetItemRender = (currentTheme) => (li, itemProps) => {
  let itemIcon = "";

  if (itemProps.dataItem.CategoryId == 1)
    //table
    itemIcon = "grid";
  else if (itemProps.dataItem.CategoryId == 2)
    //view
    itemIcon = "group";
  else if (itemProps.dataItem.CategoryId == 3)
    //SP
    itemIcon = "html";
  //"none"
  else itemIcon = "minus";

  const itemChildren = (
    <div
      className="datasets-list-item"
      style={{ display: "flex", alignItems: "center", padding: "0px 6px" }}
    >
      <div
        style={{
          marginRight: 12,
        }}
        className={`k-icon k-i-${itemIcon}`}
      ></div>
      <div>{itemProps.dataItem.Title}</div>
    </div>
  );

  return React.cloneElement(li, li.props, itemChildren);
};

const toolbarRenderer = (props) => {
  const { currentTheme, currentUser, pageState, t } = props;

  const saveTitle = pageState.mode == 1 ? t("alerts.save") : t("alerts.update");

  const toolsVisibleIcon = pageState.toolsVisible ? "unpin" : "pin";
  const toolsVisibleTitle = pageState.toolsVisible
    ? t("alerts.hide")
    : t("alerts.show");
 
  return (
    <Toolbar>
      <ToolbarItem>
        <Button
          disabled={pageState.processing}
          style={{ color: currentTheme.primary }}
          icon={toolsVisibleIcon}
          onClick={props.onToolsToggle}
        >
          {toolsVisibleTitle}
        </Button>
      </ToolbarItem>
      <ToolbarSeparator />
      {currentUser.IsAuthenticated && canCreateAlerts(currentUser) && (
        <ToolbarItem>
          <Button
            disabled={pageState.processing || alertsMaxReached(currentUser)}
            style={{ color: currentTheme.primary }}
            icon="plus"
            onClick={props.onNew}
          >
            {t("alerts.create")}
          </Button>
        </ToolbarItem>
      )}
      {!pageState.formValues.IsSystem &&
        ((pageState.mode == 1 &&
          currentUser.IsAuthenticated &&
          canCreateAlerts(currentUser)) ||
          (pageState.mode == 2 &&
            currentUser.IsAuthenticated &&
            canEditAlerts(currentUser) &&
            !pageState.formValues.IsSystem)) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={
                  pageState.processing ||
                  (pageState.mode == 1 && alertsMaxReached(currentUser))
                }
                style={{ color: currentTheme.primary }}
                icon="save"
                onClick={props.onSave}
              >
                {saveTitle}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {pageState.mode == 2 &&
        currentUser.IsAuthenticated &&
        canCreateResolvedAlerts(currentUser) &&
        canViewResolvedAlerts(currentUser) && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing || alertsMaxReached(currentUser)}
                style={{ color: currentTheme.primary }}
                icon="backward-element"
                onClick={props.onClone}
              >
                {t("alerts.clone_alert")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
      {pageState.mode == 2 &&
        currentUser.IsAuthenticated &&
        canDeleteAlerts(currentUser) &&
        !pageState.formValues.IsSystem && (
          <Fragment>
            <ToolbarSeparator />
            <ToolbarItem>
              <Button
                disabled={pageState.processing}
                style={{ color: currentTheme.primary }}
                icon="delete"
                onClick={(e) => props.onDelete(null)}
              >
                {t("alerts.deletealert")}
              </Button>
            </ToolbarItem>
          </Fragment>
        )}
    </Toolbar>
  );
};

const getEntityOperations = (expression, currentUser, t) => {
  let result = [];

  if (expression.IsSystem) {
    if (currentUser.IsAuthenticated && (canViewAlerts(currentUser) || canEditAlerts(currentUser)))
      result.push({ id: 1, text: t("alerts.view"), icon: "eye" });
  } else {
    if (currentUser.IsAuthenticated && (canViewAlerts(currentUser) || canEditAlerts(currentUser)))
      result.push({ id: 2, text: t("alerts.view"), icon: "eye" });

    if (currentUser.IsAuthenticated && canDeleteAlerts(currentUser))
      result.push({ id: 3, text: t("alerts.delete"), icon: "delete" });
  }

  return result;
};

const entityMenuItemRenderer =
  (currentTheme, entityId, onItemClicked) => (props) => {
    if (props.item.text)
      return (
        <div
          style={{ display: "flex", width: "100%", padding: "0px 6px" }}
          onClick={(e) => onItemClicked(entityId, props.item)}
        >
          <span
            className={`k-icon k-i-${props.item.icon}`}
            style={{ display: "inline-block" }}
          ></span>
          <div style={{ marginLeft: 12 }}>{props.item.text}</div>
        </div>
      );
    else return <div className="menu-separator" style={{}} />;
  };

const entitiesItemRenderer =
  (currentTheme, currentUser, t) => (props, item, index) => {
    const icon = item.IsGrouped ? "k-i-table" : "k-i-foreground-color";

    const titleFontStyle = item.IsActive ? "normal" : "italic";

    return (
      <div
        key={item.Id}
        className={`list-item`}
        onDoubleClick={(e) => {
          if (currentUser.IsAuthenticated && (canViewAlerts(currentUser) || canEditAlerts(currentUser)))
            props.onEntityDoubleClick(item.Id);
        }}
      >
        <div className={`list-item-alert-icon k-icon ${icon}`}></div>
        <div className="list-item-title" style={{ fontStyle: titleFontStyle }}>
          {item.Title}
        </div>
        {currentUser.IsAuthenticated &&
          (canViewAlerts(currentUser) || canEditAlerts(currentUser) || canDeleteAlerts(currentUser)) && (
            <div className="list-item-operations">
              <DropDownButton
                className="buttons-container-button"
                items={getEntityOperations(item, currentUser, t)}
                itemRender={entityMenuItemRenderer(
                  currentTheme,
                  item.Id,
                  (entityId, _item) => {
                    if (_item.id == 1 || _item.id == 2)
                      //view
                      props.onEntitySelected(entityId, _item);
                    else if (_item.id == 3)
                      // delete
                      props.onDelete(entityId, _item);
                  }
                )}
                icon="more-horizontal"
                fillMode={"flat"}
              />
            </div>
          )}
      </div>
    );
  };

export const AlertsPageBody = (props) => {
  const {
    onSplitterChange,
    dataSetsPanelRef,
    onDatasetChange,
    onNew,
    onSave,
    onClone,
    onDelete,
    onErrorbarClose,
    onToolsToggle,
    onEntitySelected,
    onEntityDoubleClick,
    updateDirtyState,
    onKeyChange,
    ...other
  } = props;

  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.alertsPage);
  const appearance = useSelector((state) => state.appearance);
  const datasets = pageState.lookups.datasets;
  const currentDataset = pageState.currentDataset;
  const currentDatasetId = currentDataset.Id;
  const [entityFilter, setEntityFilter] = useState("");
  const [showSystemEntities, setShowSystemEntities] = useState(true);
  const { t } = useTranslation();

  const introItems = useMemo(
    () => [
      {
        color: currentTheme.colorVariant1,
        icon: ExclamationIcon,
        width: 64,
        height: 32,
        content: t("alerts.exclamation"),
      },
      {
        color: currentTheme.colorVariant2,
        icon: HeartIcon,
        width: 90,
        height: 32,
        content: t("alerts.heart"),
      },
      {
        color: currentTheme.colorVariant2,
        icon: TachometerIcon,
        width: 100,
        height: 32,
        content: t("alerts.tachometer"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: MessageIcon,
        width: 80,
        height: 32,
        content: t("alerts.message"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: GroupIcon,
        width: 90,
        height: 32,
        content: t("alerts.group"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: FilterIcon,
        width: 90,
        height: 32,
        content: t("alerts.filter"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: BellIcon,
        width: 90,
        height: 32,
        content: t("alerts.bell"),
      },
      {
        color: currentTheme.colorVariant3,
        icon: ActivateIcon,
        width: 90,
        height: 32,
        content: t("alerts.activate"),
      },
    ],
    [t, currentTheme]
  );

  const getPanelBarItems = () => {
    const panelBarItems = [];

    panelBarItems.push(
      <PanelBarItem
        className="panel-dataset"
        key="dataset"
        title={t("alerts.dataset")}
        expanded={true}
      >
        <div className="dataset">
          <DropDownListById
            iconClassName="k-icon k-i-arrow-s k-i-table"
            style={{ marginTop: 4 }}
            data={datasets}
            dataItemKey="Id"
            textField="Title"
            value={currentDatasetId}
            onChange={onDatasetChange}
            itemRender={datasetItemRender(currentTheme)}
          />
          {currentDatasetId == 0 && (
            <div
              style={{ marginLeft: 3, color: `${currentTheme.primary}` }}
              className="k-form-hint k-text-center"
            >
              {t("alerts.select")}
            </div>
          )}
        </div>
      </PanelBarItem>
    );

    if (currentDataset.Id > 0) {
      panelBarItems.push(
        <PanelBarItem
          className="panel-entities"
          key="alerts"
          title={t("alerts.alerts")}
          selected={false}
          expanded={false}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Input
              type="search"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.value)}
              style={{
                width: "calc(100% - 16px)",
                margin: "16px 8px 8px 8px",
                backgroundColor: "transparent",
              }}
              placeholder={t("alerts.search")}
            />
            <div style={{ marginTop: 2, marginBottom: 16, marginLeft: 10 }}>
              <Checkbox
                style={{ marginTop: -4, marginRight: 4 }}
                value={showSystemEntities}
                onChange={(e) => {
                  setShowSystemEntities(e.value);
                }}
              >
                {" "}
                {t("alerts.showalert")}
              </Checkbox>
            </div>
            <List
              className="alerts"
              items={currentDataset.Alerts.filter((e) => {
                if (
                  (!entityFilter || entityFilter.trim() == "") &&
                  showSystemEntities
                )
                  return true;

                if (!entityFilter || entityFilter.trim() == "")
                  //do not show system entities
                  return e.IsSystem == false;

                //i am here => there is text
                const filter = entityFilter.toLowerCase();

                if (showSystemEntities)
                  return e.Title.toLowerCase().indexOf(filter) >= 0;
                else
                  return (
                    e.Title.toLowerCase().indexOf(filter) >= 0 &&
                    e.IsSystem == false
                  );
              })}
              itemRenderer={entitiesItemRenderer(currentTheme, currentUser, t)}
              onEntitySelected={onEntitySelected}
              onEntityDoubleClick={onEntityDoubleClick}
              onDelete={onDelete}
            />
          </div>
        </PanelBarItem>
      );

      panelBarItems.push(
        <PanelBarItem
          className="panel-description"
          key="help"
          title={t("alerts.description")}
          selected={false}
          expanded={false}
          style={{}}
        >
          <p className="description" style={{ padding: "6px 12px", margin: 0 }}>
            {currentDataset.Description}
          </p>
        </PanelBarItem>
      );
    }

    return panelBarItems;
  };

  return (
    <Fragment>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {pageState.pageLoaded && (
        <Splitter
          style={{
            border: "none",
            flexDirection: appearance.isRightToLeft ? "row-reverse" : "row",
          }}
          panes={pageState.panes}
          onChange={onSplitterChange}
        >
          <div className="alerts-page-manager">
            <PanelBar
              ref={dataSetsPanelRef}
              style={{
                border: "none",
                direction: appearance.isRightToLeft ? "rtl" : "ltr",
              }}
              expanded={[".0", ".1", ".2"]}
            >
              {getPanelBarItems()}
            </PanelBar>
          </div>
          {currentDatasetId > 0 && (
            <Form
              className="alerts-page-form"
              currentTheme={currentTheme}
              currentUser={currentUser}
              toolbarRenderer={toolbarRenderer}
              t={t}
              onNew={onNew}
              onSave={onSave}
              onClone={onClone}
              onDelete={onDelete}
              errorMessages={pageState.formServerErrors}
              onErrorbarClose={onErrorbarClose}
              onToolsToggle={onToolsToggle}
              pageState={pageState}
            >
              {pageState.mode > 0 ? (
                <AlertsPageForm
                  onUsersCheck={props.onUsersCheck}
                  onUserCheck={props.onUserCheck}
                  onGroupsCheck={props.onGroupsCheck}
                  onGroupCheck={props.onGroupCheck}
                  onGroupedFieldCheck={props.onGroupedFieldCheck}
                  onGroupedFieldsCheck={props.onGroupedFieldsCheck}
                  onKeyChange={onKeyChange}
                  updateDirtyState={updateDirtyState}
                />
              ) : (
                <Fragment></Fragment>
              )}
            </Form>
          )}
          {currentDatasetId == 0 && (
            <IntroPage title={t("alerts.alerts")} items={introItems} />
          )}
        </Splitter>
      )}
      <style>
        {`
                    .alerts-page > .content
                    {
                    } 

                    .alerts-page .k-splitter{
                      flex-direction:${
                        appearance.isRightToLeft ? "row-reverse" : "row"
                      } 
                    }
                      
                    .alerts-page .k-splitter .k-pane{
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 
                    }

                    .alerts-page .datasetview-page-wrapper{
                    border: ${currentTheme.pageFormBorder};   
                    height:100%; direction:${
                      appearance.isRightToLeft ? "rtl" : "ltr"
                    } 
                    
                    }  

                    /*the manager*/
                    .alerts-page > .content .alerts-page-manager
                    {
                      border: ${currentTheme.managerBorder};
                      border-radius: ${currentTheme.pageBorderRadius};
                      background-color: ${currentTheme.componentBgFaded};
                    }

                    .alerts-page > .content .alerts-page-manager .k-dropdownlist
                    {
                      /*bg of datasets dropdown*/
                      background-color: transparent;
                      color: ${currentTheme.componentText};
                    }

                    .alerts-page > .content .alerts-page-manager .k-dropdownlist:focus
                    {
                        box-shadow: none;   
                    }

                    .alerts-page > .content .alerts-page-manager .panel-entities .k-input::placeholder
                    {
                        color: ${currentTheme.componentText};
                        /*font-style: italic;*/
                    }

                    .alerts-page > .content .alerts-page-manager .k-panelbar
                    {
                      background-color: transparent;
                    }

                    .alerts-page > .content .alerts-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link .k-icon
                    {
                      /*the arrow near every panel header*/
                      color: ${currentTheme.touchColor}!important;
                      margin-right: 6px;
                    }

                    .alerts-page > .content .alerts-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link
                    {
                      /*the span in the above <li> i.e. the title of every group in the manager: datasets, alerts and description*/  
                      background-color: transparent;
                      color: ${currentTheme.touchColor};
                      border-bottom: 1px solid ${currentTheme.separatorColor}; 
                    }

                    .alerts-page > .content .alerts-page-manager ul li.k-panelbar-header.k-panelbar-item .k-link:hover
                    {
                      /*the above hovered - do nothing*/
                    }

                    .alerts-page > .content .alerts-page-manager .alerts .list-item:hover
                    {
                      /*expression item hover */
                      background-color: ${currentTheme.componentBorderColor};
                      cursor: pointer;
                    }

                    .alerts-page > .content .alerts-page-manager .dataset,
                    .alerts-page > .content .alerts-page-manager .description,
                    .alerts-page > .content .alerts-page-manager .k-child-animation-container
                    {
                      /*bg color of exp. manager elements */
                    }

                    .alerts-page > .content .alerts-page-manager .list-container
                    {
                      /*bg of both the exp. and columns Lists*/
                      background-color: transparent; 
                      padding: 0;
                    }

                    .alerts-page > .content .alerts-page-manager .k-panelbar-header
                    {
                      background-color: transparent; 
                    }

                    .alerts-page > .content .alerts-page-manager .k-panelbar-content
                    {
                      background-color: transparent;
                    }

                    .alerts-page > .content .alerts-page-manager .alerts .list-item-alert-icon
                    {
                      /*first icon in alerts list*/
                      padding-left: 12px
                    }

                    .alerts-page > .content .alerts-page-manager .alerts .list-item-title
                    {
                      vertical-align: middle;
                    }               


                    /*the form*/
                    .alerts-page-form
                    {
                      background-color: transparent;
                      padding-left: 0px;
                      border: ${currentTheme.pageFormBorder};   
                      border-radius: ${currentTheme.pageBorderRadius};
                    }   

                    /*form toolbar*/
                    .alerts-page-form > .toolbar .k-toolbar
                    {
                      width: 100%;
                      border-left: none !important;
                      border-bottom: 1px solid ${currentTheme.separatorColor};
                      border-top: none !important;
                      border-right: none !important;
                      background-color: ${
                        currentTheme.toolbarBg
                      };/*transparent;*/
                      direction:${appearance.isRightToLeft ? "rtl" : "ltr"} 

                    }

                    .alerts-page > .content .toolbar .k-button
                    {
                      /*the toolbar buttons*/
                      border: none;
                      padding: 6px 8px;
                    }

                    .alerts-page > .content .toolbar .k-button-text
                    {
                      /*color of the text in every toolbar button=black in azure theme*/
                      color: ${currentTheme.toolbarText};
                    }

                    .alerts-page > .content .toolbar .k-button-icon
                    {
                      font-weight: 500;
                      color: ${currentTheme.primary};
                    }

                    .alerts-page > .content .form-root .content
                    {
                      background-color: ${currentTheme.pageContentBg};
                    }
                    

                    /*styling the popup menu */
                    .k-popup ul{
                        padding: 0px;
                    }

                    .k-popup ul > li{
                        margin: 0;
                        padding: 0px;                                
                    }

                    .k-popup ul > li > span{
                        border: none !important;
                        box-shadow: none !important;
                    }

                    .k-menu-link{
                        padding: 0px 0px !important;                        
                    }

                    @media (max-width: 1024px) {
                      .alerts-page > .content {
                        overflow-y: auto;
                      }

                      .alerts-page > .content .k-splitter {
                        display: flex;
                        flex-direction: column !important;
                        height: auto !important;
                        margin-bottom: calc(64px + env(safe-area-inset-bottom));
                        overflow: visible;
                      }

                      .alerts-page > .content .k-splitbar {
                        display: none !important;
                      }

                      .alerts-page > .content .k-splitter > .k-pane {
                        flex: 0 0 auto !important;
                        width: 100% !important;
                        height: auto !important;
                        min-height: 0 !important;
                        min-width: 0 !important;
                        max-width: 100% !important;
                      }

                      .alerts-page > .content .alerts-page-manager {
                        height: auto;
                        max-height: 45dvh;
                        margin-bottom: 12px;
                        overflow: auto;
                      }

                      .alerts-page > .content .alerts-page-manager .alerts .list-item-title {
                        flex: 1;
                        min-width: 0;
                        max-width: none;
                      }

                      .alerts-page > .content .alerts-page-form {
                        height: auto;
                        min-height: 0;
                        overflow: visible;
                      }

                      .alerts-page > .content .alerts-page-form > .toolbar .k-toolbar {
                        flex-wrap: wrap;
                        gap: 4px;
                        padding: 6px;
                      }

                      .alerts-page > .content .alerts-page-form > .toolbar {
                        display: none;
                      }

                      .alerts-page > .content .alerts-page-form > .content {
                        overflow: auto;
                        pointer-events: auto;
                        padding-bottom: calc(80px + env(safe-area-inset-bottom));
                        box-sizing: border-box;
                      }

                      .alerts-page > .content .alerts-page-form > .content input,
                      .alerts-page > .content .alerts-page-form > .content textarea,
                      .alerts-page > .content .alerts-page-form > .content button,
                      .alerts-page > .content .alerts-page-form > .content .k-dropdownlist,
                      .alerts-page > .content .alerts-page-form > .content .toggle-container,
                      .alerts-page > .content .alerts-page-form > .content .list-item,
                      .alerts-page > .content .alerts-page-form > .content .list-item-delete {
                        pointer-events: none;
                      }

                      .alerts-page > .content .alerts-page-form > .toolbar .k-separator {
                        display: none;
                      }

                      .alerts-page > .content .intro-page-wrapper {
                        height: auto;
                        min-height: 0;
                        margin-bottom:100px;
                      }

                      .alerts-page > .content .intro-page {
                        overflow: visible;
                        padding: 8px;
                      }

                      .alerts-page > .content .intro-page > .item {
                        width: 50%;
                        padding: 16px 10px;
                        margin-top: 0;
                      }
                    }
                  `}
      </style>
    </Fragment>
  );
};
